import {
  addDays,
  format,
  addMinutes,
  isBefore,
  isValid,
  lastDayOfMonth,
  parseISO,
  startOfDay,
} from 'date-fns';

import { ICeDocument, ICeEntry } from '~/services/api/communityEngagement/types';
import {
  dateRequestFormat,
  dayPadded,
  generalDate,
  monthDay,
  monthDayPadded,
  monthDayYear,
  monthDayYearPadded,
  monthYear,
} from '~/ui/constants/dateFormat';

export const isoToFormat = (date: string, dateFormat = generalDate): string =>
  format(new Date(date), dateFormat);

export const getLocalDateNoFormat = (date: string | Date): Date => {
  const tempDate = new Date(date);
  return addMinutes(tempDate, tempDate.getTimezoneOffset());
};

export const isoToLocal = (date: string, dateFormat = generalDate): string =>
  format(getLocalDateNoFormat(date), dateFormat);

export const normalizeDateString = (date: string | Date, endDay?: boolean): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  return `${(newDate as string).split('T')[0]}T${endDay ? '23:59:00' : '00:00:00'}Z`;
};

/** Calendar date from `date`, time of day = current moment in UTC (`…Z`). */
export const normalizeDateStringWithCurrentUtcTime = (date: string | Date): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  const datePart = (newDate as string).split('T')[0];
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const timePart = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(
    now.getUTCSeconds(),
  )}`;

  return `${datePart}T${timePart}Z`;
};

export const setNoon = (date: string | Date): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  return `${(newDate as string).split('T')[0]}T12:00:00Z`;
};

/**
 * True when “now” is on or after 00:00 of the calendar day after `endDate`
 * (using local start-of-day after `setNoon` normalization).
 */
export const isExpiredByEndDate = (endDate: string): boolean => {
  const endDay = startOfDay(new Date(setNoon(endDate)));
  return !isBefore(new Date(), addDays(endDay, 1));
};

export const destructHours = (hours: string): Date => {
  const date = new Date();
  const time = hours.split(':');
  date.setHours(Number(time[0]), Number(time[1]), Number(time[2]));

  return date;
};

export const getCombinedDateTime = (date: string, time?: string): Date =>
  time
    ? new Date(`${date?.split('T')[0]}T${time?.split('T')[1]}`)
    : new Date(`${date?.split('T')[0]}T${new Date().toISOString()?.split('T')[1]}`);

export const removeTime = (date?: string): string | undefined =>
  date ? date.split('T')?.[0] : undefined;

/** Local calendar day of `date` as `yyyy-MM-dd`. */
export const formatDay = (date: Date): string => format(date, dateRequestFormat);

export const toDay = (value: string): string | null => {
  const date = parseISO(value);
  return isValid(date) ? formatDay(date) : null;
};

export interface IYearMonth {
  year: number;
  month: number;
}

const MONTH_PARAM = /^(\d{4})-(\d{2})$/;

export const toYearMonth = (date: Date): IYearMonth => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
});

export const compareYearMonth = (a: IYearMonth, b: IYearMonth): number =>
  a.year * 12 + a.month - (b.year * 12 + b.month);

export const shiftYearMonth = ({ year, month }: IYearMonth, by: number): IYearMonth => {
  const index = year * 12 + (month - 1) + by;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};

export const parseMonthParam = (value: string | null, today: Date): IYearMonth => {
  const current = toYearMonth(today);
  const match = value ? MONTH_PARAM.exec(value) : null;
  if (!match) return current;

  const parsed = { year: Number(match[1]), month: Number(match[2]) };
  return parsed.month < 1 || parsed.month > 12 ? current : parsed;
};

export const formatMonthParam = ({ year, month }: IYearMonth): string =>
  `${year}-${String(month).padStart(2, '0')}`;

export const firstDayOf = ({ year, month }: IYearMonth): Date => new Date(year, month - 1, 1);

export const formatPeriod = (yearMonth: IYearMonth): string => {
  const start = firstDayOf(yearMonth);
  return `Period: ${format(start, monthDayYear)} - ${format(lastDayOfMonth(start), monthDayYear)}`;
};

export const formatReported = ({
  startDate,
  endDate,
}: Pick<ICeEntry, 'startDate' | 'endDate'>): string => {
  const start = format(parseISO(startDate), monthDayPadded);
  if (endDate === startDate) return `Reported ${start}`;
  return `Reported ${start} - ${format(parseISO(endDate), dayPadded)}`;
};

/** `2027-02-03` → `Archived Feb 03` */
export const formatArchived = (isoDate: string): string =>
  `Archived ${format(parseISO(isoDate), monthDayPadded)}`;

export const formatDateRange = (startDate: string, endDate: string | null): string => {
  const start = parseISO(startDate);
  if (!endDate) return `${format(start, monthDayYear)} - No end date`;

  const end = parseISO(endDate);
  const startFormat = start.getFullYear() === end.getFullYear() ? monthDay : monthDayYear;
  return `${format(start, startFormat)} - ${format(end, monthDayYear)}`;
};

/** `Jane Doe · Feb 03, 2027` */
export const formatUploaded = ({
  uploadedBy,
  uploadedOn,
}: Pick<ICeDocument, 'uploadedBy' | 'uploadedOn'>): string =>
  `${uploadedBy} · ${isoToFormat(uploadedOn, monthDayYearPadded)}`;

/** `2027-08-15` → `Aug 15, 2027` */
export const formatLongDay = (isoDate: string): string => format(parseISO(isoDate), monthDayYear);

export const formatMonth = (date: string | null): string | null =>
  date ? format(parseISO(date), monthYear) : null;

/**
 * Converts duration from "days.hours:minutes:seconds" or "hours:minutes:seconds" to "Xh Ym".
 * E.g. "23.19:26:45" -> "571h 26m" (23*24 + 19 hours), "19:26:45" -> "19h 26m"
 */
export const formatDurationToHours = (timeString: string): string => {
  if (!timeString?.trim()) return '0h 0m';

  const withDays = timeString.match(/^(\d+)\.(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
  const withoutDays = timeString.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);

  let totalHours: number;
  let minutes: number;

  if (withDays) {
    const days = parseInt(withDays[1], 10);
    const hours = parseInt(withDays[2], 10);
    minutes = parseInt(withDays[3], 10);
    totalHours = days * 24 + hours;
  } else if (withoutDays) {
    totalHours = parseInt(withoutDays[1], 10);
    minutes = parseInt(withoutDays[2], 10);
  } else {
    return '0h 0m';
  }

  return `${totalHours}h ${minutes}m`;
};
